import { useState, useEffect } from 'react';
import OtpInput from 'react-otp-input';
import { missionApi } from '../../api/mission.api';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { Btn, Badge } from '../ui/index';
import { toPersian, formatPersianDateTime } from '../../utils/persianNumber';

const STATUS_LABELS = {
  pending_otp: { text: 'در انتظار تایید OTP', variant: 'amber' },
  approved: { text: 'تایید شده', variant: 'green' },
  no_recipient: { text: 'بدون مسئول تایید', variant: 'gray' },
};

export default function MissionOtpApprovalPanel({ ticketId, initialStatus, debugOtpHint, onUpdated }) {
  const { addToast } = useUI();
  const { user } = useAuth();
  const [status, setStatus] = useState(initialStatus);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [debugOtp, setDebugOtp] = useState(debugOtpHint || null);

  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  useEffect(() => {
    if (debugOtpHint) setDebugOtp(debugOtpHint);
  }, [debugOtpHint]);

  if (!status) return null;

  const label = STATUS_LABELS[status.approval_status] || { text: status.approval_status || '—', variant: 'gray' };
  const canResend = ['dispatcher', 'manager_employee'].includes(user?.type);

  const verify = async () => {
    if (code.length < 6) {
      addToast('کد ۶ رقمی را کامل وارد کنید', 'error');
      return;
    }
    setVerifying(true);
    try {
      const res = await missionApi.verifyOtp({ mission_id: status.mission_id, otp_code: code });
      if (res.data?.ok) {
        addToast('ماموریت با موفقیت تایید شد', 'success');
        const next = { ...status, approval_status: 'approved', otp_approved_at: new Date().toISOString() };
        setStatus(next);
        onUpdated?.(next);
        setCode('');
        setDebugOtp(null);
      }
    } catch (err) {
      addToast(err.response?.data?.error || 'کد وارد شده صحیح نیست', 'error');
    }
    setVerifying(false);
  };

  const resend = async () => {
    setResending(true);
    try {
      const res = await missionApi.resendOtp(status.mission_id);
      const data = res.data || {};
      addToast(data.sms_sent ? 'کد مجدداً ارسال شد' : 'کد صادر شد (SMS در دسترس نبود)', data.sms_sent ? 'success' : 'info');
      if (data.debug_otp) setDebugOtp(data.debug_otp);
      setStatus((prev) => ({ ...prev, approval_status: 'pending_otp', sms_sent: data.sms_sent }));
    } catch (err) {
      addToast(err.response?.data?.error || 'خطا در ارسال مجدد کد', 'error');
    }
    setResending(false);
  };

  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 14, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: '#92400e', margin: 0 }}>تایید OTP ماموریت</h4>
        <Badge variant={label.variant}>{label.text}</Badge>
      </div>

      {status.recipient_name && (
        <p style={{ fontSize: 12, color: '#78350f', margin: '0 0 10px', lineHeight: 1.7 }}>
          کد تایید به <strong>{status.recipient_name}</strong>
          {status.recipient_phone ? ` (${status.recipient_phone})` : ''} ارسال شده است.
        </p>
      )}

      {status.approval_status === 'no_recipient' && (
        <p style={{ fontSize: 12, color: '#78350f', margin: 0, lineHeight: 1.7 }}>
          برای شرکت «{status.company_display || status.company_name || '—'}» مسئول تایید ماموریت تعریف نشده است. دیسپچر باید از صفحه شرکت، مسئول دریافت/تایید را اضافه کند.
        </p>
      )}

      {status.approval_status === 'approved' && status.otp_approved_at && (
        <p style={{ fontSize: 12, color: '#166534', margin: 0 }}>
          زمان تایید: {formatPersianDateTime(status.otp_approved_at)}
        </p>
      )}

      {status.approval_status === 'pending_otp' && status.otp_expires_at && (
        <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 10px' }}>
          اعتبار کد تا: {formatPersianDateTime(status.otp_expires_at)}
        </p>
      )}

      {status.approval_status === 'pending_otp' && (
        <>
          <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 14px' }}>
            مسئول شرکت یا دیسپچر می‌تواند کد ۶ رقمی دریافتی را وارد کند.
          </p>
          <div style={{ direction: 'ltr', display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
            <OtpInput
              value={code}
              onChange={setCode}
              numInputs={6}
              renderInput={(props) => (
                <input {...props} style={{
                  width: 40, height: 44, margin: '0 4px', fontSize: 18, borderRadius: 8,
                  border: '1.5px solid #fcd34d', textAlign: 'center', fontFamily: 'inherit',
                }} />
              )}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn onClick={verify} loading={verifying}>تایید ماموریت</Btn>
            {canResend && (
              <Btn variant="secondary" onClick={resend} loading={resending}>ارسال مجدد کد</Btn>
            )}
          </div>
          {debugOtp && (
            <p style={{ fontSize: 11, color: '#b45309', margin: '12px 0 0', lineHeight: 1.6 }}>
              {/* TESTING ONLY — shown when SMS could not be delivered */}
              <strong>تست:</strong> کد OTP = {toPersian(debugOtp)} (فقط برای محیط توسعه)
            </p>
          )}
        </>
      )}
    </div>
  );
}
